import { Omit } from '@futrli/shared-services';
import classNames from 'classnames';
import { endOfMonth, endOfWeek, isBefore, isEqual } from 'date-fns';
import { isNil } from 'lodash';
import React, { useState } from 'react';

import { PeriodType, PERIOD_TYPE_OPTIONS, SIZES } from '../../../../core/constants';
import { forceLocalDate } from '../../../../utils/dates';
import { CellProps } from '../cell';
import { NumberExplained } from '../number-explained/number-explained';
import { HighlightedDifference } from './highlighted-difference';

import styles from './current-period.scss';

/* eslint-disable complexity */

interface OwnProps {
  canonicalCategory?: string;
  cellComponent: React.ReactNode;
  hidePeriodToDate?: boolean;
  hideDifference?: boolean;
  isAssumption: boolean;
  isEditable?: boolean;
  periodToDateValue?: string;
  predictedValue: string;
}

export type CurrentPeriodProps = OwnProps & Omit<CellProps, 'value'>;

export function isCurrentPeriod(date: string, periodType: PeriodType, calculatedDate?: string | null): boolean {
  const dateToCheck = calculatedDate ? forceLocalDate(calculatedDate) : new Date();
  switch (periodType) {
    case PERIOD_TYPE_OPTIONS.WEEKLY_MONDAY:
      return isEqual(endOfWeek(forceLocalDate(date), { weekStartsOn: 1 }), endOfWeek(dateToCheck, { weekStartsOn: 1 }));
    case PERIOD_TYPE_OPTIONS.WEEKLY_SUNDAY:
      return isEqual(endOfWeek(forceLocalDate(date)), endOfWeek(dateToCheck));
    default:
      return isEqual(endOfMonth(forceLocalDate(date)), endOfMonth(dateToCheck));
  }
}

export function isPastCell(cellDate: string, periodType: PeriodType, calculatedDate?: string | null): boolean {
  const dateToCheck = calculatedDate ? forceLocalDate(calculatedDate) : new Date();
  if (periodType === PERIOD_TYPE_OPTIONS.MONTHLY) {
    return isBefore(endOfMonth(forceLocalDate(cellDate)), dateToCheck);
  } else {
    const weeklyOffset = periodType === PERIOD_TYPE_OPTIONS.WEEKLY_MONDAY ? 1 : 0;
    return isBefore(endOfWeek(forceLocalDate(cellDate), { weekStartsOn: weeklyOffset }), dateToCheck);
  }
}

const isNaNorNil = (value: string | undefined) => isNil(value) || isNaN(Number(value));

export const CurrentPeriod: React.FC<CurrentPeriodProps> = ({
  canonicalCategory,
  cellComponent,
  hideDifference,
  hidePeriodToDate,
  index,
  isAssumption,
  isDisabled,
  isLevel1,
  isLast,
  isExpanded,
  isFirst,
  isChild,
  periodToDateValue,
  predictedValue,
  rowHeight,
  rowHeightOverride,
  numberExplained,
  joinedCell,
  category
}) => {
  const [anchorNumberExplained, setAnchorNumberExplained] = useState<HTMLDivElement | null>(null);
  const [openNumberExplained, setOpenNumberExplained] = useState<boolean>(false);
  const cellHeight = rowHeightOverride || rowHeight;
  const isSmall = cellHeight === SIZES.SMALL;
  const validValues = !isNaNorNil(periodToDateValue) && !isNaNorNil(predictedValue);
  const showDifference = !hideDifference && !isAssumption && !isSmall && !hidePeriodToDate && validValues;
  const idNumberExplained = index;
  const allowedNumberExplained = Boolean(numberExplained?.allowed);

  const categoryName = category ?? '';

  const classes = classNames({
    [styles.currentPeriodCell]: true,
    [styles.isChild]: isChild,
    [styles.isLevel1]: isLevel1,
    [styles.isLast]: isLast,
    [styles.isFirst]: isFirst,
    [styles.isExpanded]: isExpanded,
    [styles.isDisabled]: isDisabled,
    [styles.withChart]: [SIZES.X_LARGE, SIZES.LARGE].includes(cellHeight),
    [styles.hasJoinedCell]: joinedCell,
    [styles.highlight]: allowedNumberExplained && (anchorNumberExplained !== null),
    [styles[categoryName]]: !!category
  });

  const predictedClasses = classNames({
    [styles.predicted]: true
  });

  const handleOpenNumberExplained = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setAnchorNumberExplained(event.currentTarget);
    setOpenNumberExplained(true);
  };

  const handleCloseNumberExplained = () => {
    setAnchorNumberExplained(null);
    setOpenNumberExplained(false);
  };

  const renderNumberExplained = () => {
    if (!numberExplained?.data) {
      return;
    }

    return (
      <NumberExplained
        id={idNumberExplained}
        anchorEl={anchorNumberExplained}
        open={openNumberExplained}
        onClose={handleCloseNumberExplained}
        {...numberExplained.data}
        actual={Number(periodToDateValue)}
        predicted={Number(predictedValue)}
      />
    );
  };

  return (
    <div className={classes} onClick={handleOpenNumberExplained}>
      <div className={predictedClasses}>
        {cellComponent}
        {showDifference && (
          <HighlightedDifference
            periodToDateAmount={Number(periodToDateValue)}
            predictedAmount={Number(predictedValue)}
            canonicalCategory={canonicalCategory}
          />
        )}
      </div>
      {allowedNumberExplained && renderNumberExplained()}
    </div>
  );
};
